/**
 * Math-backed estimate for how long a respondent's interview will take,
 * rendered on the join screen under "We'll guide you through a short
 * conversation." The respondent has already validated their link, the
 * inputs that drive this estimate (`fieldCount`, `probingDepth`) come
 * from `/api/room/validate` (and `resume`); the raw `form_schema` array
 * never leaves the server, so the question content is not disclosed.
 *
 * Per-depth coefficients are calibrated to two product-owner anchors:
 *   - 3 questions x Surface  -> ~90s  -> "Less than 2 min"
 *   - 5 questions x Balanced -> ~325s -> "About 5 min"
 *
 * Coefficients were trimmed again (2026) after testing showed real calls
 * ending shorter than the estimate, so the estimate now leans more
 * optimistic. Tweak them further as we collect real session-duration data.
 */

/**
 * Average wall-clock seconds spent on a single question (ask + respondent
 * answer + any follow-ups dictated by the probing depth). Indexed by the
 * 4-stop depth value used in {@link components/ui/ProbingDepthControl}.
 */
const SECONDS_PER_QUESTION_BY_DEPTH: Record<1 | 2 | 3 | 4, number> = {
  1: 30,   // Surface  - one ask + answer, no follow-ups
  2: 42,   // Light    - occasional clarifier on vague answers
  3: 65,   // Balanced - probes important answers; accepts clear ones
  4: 100,  // Deep     - 3-5 follow-ups per topic
};

/** Default depth when the column is null on the session row. */
const DEFAULT_DEPTH: 1 | 2 | 3 | 4 = 3;

/**
 * Clamp a (possibly null/NaN/out-of-range) depth value into the 1-4 stop
 * set used by the rest of the codebase. Mirrors the clamp in
 * {@link components/ui/ProbingDepthControl} and the live-session worker.
 */
function clampDepth(depth: number): 1 | 2 | 3 | 4 {
  if (!Number.isFinite(depth)) return DEFAULT_DEPTH;
  const rounded = Math.round(depth);
  if (rounded <= 1) return 1;
  if (rounded >= 4) return 4;
  return rounded as 2 | 3;
}

/**
 * Estimate the total interview duration in seconds.
 *
 * Returns `0` when there are no fields (caller decides whether to render
 * anything for the empty case). Negative or non-finite `fieldCount` is
 * treated as 0 rather than throwing - the join screen never blocks on
 * this estimate, so a corrupt input degrades to "no estimate shown"
 * rather than a crash.
 */
export function estimateDurationSeconds(
  fieldCount: number,
  probingDepth: number,
): number {
  if (!Number.isFinite(fieldCount) || fieldCount <= 0) return 0;
  const depth = clampDepth(probingDepth);
  return Math.round(fieldCount * SECONDS_PER_QUESTION_BY_DEPTH[depth]);
}

/**
 * Threshold (in seconds) below which the join screen prefers a ceiling-
 * style "Less than 2 min" phrasing over the exact "About X min" form.
 * Anything 2 minutes or under reads as "Less than 2 min" so we never
 * advertise a 90-second call as "About 1 min" and undersell the
 * setup cost.
 */
const SHORT_CALL_THRESHOLD_SECONDS = 120;

/**
 * Format the estimate for display on the join screen.
 *
 * Returns `null` when there's nothing meaningful to render (no fields).
 * The output is a fully-formed sentence (capitalized, terminating
 * period) so the caller can drop it next to the existing sub-copy and
 * inherit the same paragraph styling - no second `<p>` with a smaller
 * font, no separate caption affordance.
 *
 * Short calls (≤ 2 min) render as "It will be less than 2 min." - a
 * soft ceiling that tolerates the natural variance of a short
 * conversation. Longer calls render as "It will be about X min."
 * rounded to the nearest minute.
 */
export function formatEstimatedDuration(
  fieldCount: number,
  probingDepth: number,
): string | null {
  const seconds = estimateDurationSeconds(fieldCount, probingDepth);
  if (seconds <= 0) return null;
  if (seconds <= SHORT_CALL_THRESHOLD_SECONDS) {
    return 'It will be less than 2 min.';
  }
  const minutes = Math.round(seconds / 60);
  return `It will be about ${minutes} min.`;
}

/**
 * Compact variant of {@link formatEstimatedDuration} for inline use inside a
 * sentence (for example "It takes ~8 min."). Returns a bare phrase with no
 * leading clause and no trailing period, or `null` when there's nothing to
 * render.
 *
 * Short calls (≤ 2 min) render as "under 2 min" - the same soft ceiling as the
 * long form, so we never undersell the setup cost. Longer calls render as
 * "~N min" rounded to the nearest minute.
 */
export function formatEstimatedDurationShort(
  fieldCount: number,
  probingDepth: number,
): string | null {
  const seconds = estimateDurationSeconds(fieldCount, probingDepth);
  if (seconds <= 0) return null;
  if (seconds <= SHORT_CALL_THRESHOLD_SECONDS) {
    return 'under 2 min';
  }
  const minutes = Math.round(seconds / 60);
  return `~${minutes} min`;
}
